import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Flex, Heading, VStack, Text, Input, FormControl, FormLabel,
  Spinner, Badge, Alert, AlertIcon, HStack, Icon,
} from '@chakra-ui/react';
import { FiVideo, FiMic, FiUser } from 'react-icons/fi';

export default function JoinRoom() {
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [joining, setJoining] = useState(false);

  const existingToken = searchParams.get('token');

  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const res = await fetch(`/api/v1/rooms/${roomId}/info`);
        if (res.ok) {
          setRoom(await res.json());
        } else {
          setError('Room not found or no longer available.');
        }
      } catch (e) {
        setError('Unable to reach the server.');
      } finally {
        setLoading(false);
      }
    };
    fetchRoom();
  }, [roomId]);

  const extractUserIdFromToken = (tok) => {
    try {
      let base64 = tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      while (base64.length % 4) base64 += '=';
      return JSON.parse(atob(base64)).user_id || null;
    } catch {
      return null;
    }
  };

  const handleJoin = async () => {
    setJoining(true);
    let token = existingToken;
    // If using a pre-issued token (e.g. a personalized invite), its user_id
    // is authoritative. Otherwise this is the public shareable-link path —
    // the server mints a fresh identity via self-join-token below; it never
    // trusts a client-generated id (see self_join_token in meet/views.py).
    let userId = existingToken ? extractUserIdFromToken(existingToken) : null;

    if (!token) {
      try {
        const res = await fetch(`/api/v1/rooms/${roomId}/self-join-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ display_name: displayName || 'Guest' }),
        });
        if (res.ok) {
          const data = await res.json();
          token = data.token;
          userId = data.user_id;
        } else {
          setError('Unable to join this room. It may require an invitation.');
          setJoining(false);
          return;
        }
      } catch (e) {
        setError('Failed to get join token.');
        setJoining(false);
        return;
      }
    }

    navigate(`/room?roomId=${roomId}&token=${token}&displayName=${encodeURIComponent(displayName || 'Guest')}&userId=${userId}`);
  };

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.900">
        <Spinner size="xl" color="teal.400" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.900">
        <Box maxW="400px" w="full">
          <Alert status="error" borderRadius="xl">
            <AlertIcon />
            {error}
          </Alert>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex minH="100vh" align="center" justify="center" bg="gray.900">
      <Box
        bg="gray.800"
        borderRadius="2xl"
        p={10}
        maxW="440px"
        w="full"
        boxShadow="2xl"
        border="1px solid"
        borderColor="gray.700"
      >
        <VStack spacing={6}>
          <Flex align="center" gap={3}>
            <Box
              w={12} h={12}
              borderRadius="full"
              overflow="hidden"
              bg="teal.500"
              p="2px"
              flexShrink={0}
            >
              <img src="/static/selwyn-logo.png" alt="Selwyn" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            </Box>
            <VStack align="start" spacing={0}>
              <Heading size="md" color="white">{room?.name}</Heading>
              <HStack>
                <Badge colorScheme={room?.room_type === 'webinar' ? 'purple' : 'teal'} fontSize="xs">
                  {room?.room_type}
                </Badge>
                <Text fontSize="xs" color="gray.400">
                  {room?.participant_count} / {room?.max_participants} participants
                </Text>
              </HStack>
            </VStack>
          </Flex>

          <Box w="full" borderTop="1px solid" borderColor="gray.700" pt={6}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel color="gray.300">Your Name</FormLabel>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your display name"
                  bg="gray.700"
                  border="1px solid"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: 'gray.400' }}
                  focusBorderColor="teal.400"
                  leftIcon={<FiUser />}
                />
              </FormControl>

              <HStack w="full" spacing={3}>
                <Flex
                  flex={1} justify="center" align="center" gap={2} py={3}
                  bg="gray.700" borderRadius="lg" color="gray.300" fontSize="sm"
                >
                  <FiVideo size={16} /> Camera
                </Flex>
                <Flex
                  flex={1} justify="center" align="center" gap={2} py={3}
                  bg="gray.700" borderRadius="lg" color="gray.300" fontSize="sm"
                >
                  <FiMic size={16} /> Microphone
                </Flex>
              </HStack>

              <Button
                colorScheme="teal"
                size="lg"
                w="full"
                onClick={handleJoin}
                isLoading={joining}
                loadingText="Joining..."
              >
                Join {room?.room_type === 'webinar' ? 'Webinar' : 'Meeting'}
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Box>
    </Flex>
  );
}
